import { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // CRITICAL: If returning from Emergent OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the JWT first.
    if (typeof window !== "undefined" && window.location.hash && window.location.hash.includes("session_id=")) {
      setLoading(false);
      return;
    }
    const token = localStorage.getItem("coachiq_token");
    if (!token) { setLoading(false); return; }
    api.get("/auth/me").then(r => setUser(r.data)).catch(() => localStorage.removeItem("coachiq_token")).finally(() => setLoading(false));
  }, []);

  const setSessionFromToken = async (token, u) => {
    localStorage.setItem("coachiq_token", token);
    if (u) setUser(u);
    const me = await api.get("/auth/me");
    setUser(me.data);
    return me.data;
  };

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("coachiq_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const signup = async (name, email, password, referral, consent = {}) => {
    const url = referral ? `/auth/signup?referral=${encodeURIComponent(referral)}` : "/auth/signup";
    const { data } = await api.post(url, {
      name,
      email,
      password,
      over_18_confirmed: !!consent.over_18_confirmed,
      authority_confirmed: !!consent.authority_confirmed,
      terms_accepted: !!consent.terms_accepted,
    });
    localStorage.setItem("coachiq_token", data.token);
    setUser(data.user);
    // Only seed demo data if using the explicit demo account
    if (email.toLowerCase() === "coach1@test.com") {
      try { await api.post("/auth/seed-demo"); } catch (e) { /* ignore */ }
    }
    const me = await api.get("/auth/me");
    setUser(me.data);
    return me.data;
  };

  const logout = () => {
    localStorage.removeItem("coachiq_token");
    setUser(null);
  };

  const refresh = async () => {
    const me = await api.get("/auth/me");
    setUser(me.data);
  };

  return <AuthCtx.Provider value={{ user, loading, login, signup, logout, refresh, setSessionFromToken }}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
